import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { colorFillColor } from 'mt-react-theme-manager';

import Icon from '../../../../components/Icon';
import readScopesDescription, {
  excludeReadScopes
} from './readScopesDescription';
import './styles.scss';

interface ScopeProps {
  icon: string;
  description: string;
}

function Scope(props: ScopeProps) {
  return (
    <div className="scope">
      <Icon className={colorFillColor('primary')} icon={props.icon} />
      <span>{props.description}</span>
    </div>
  );
}

function moveGuestReadToBeginningOfTheList(scopes: string[]) {
  const index = scopes.indexOf('guest_read');

  if (index === -1) return scopes;

  scopes.splice(index, 1);
  scopes.unshift('guest_read');

  return scopes;
}

interface ScopesProps {
  scopes: string[];
}

export default function Scopes(props: ScopesProps) {
  const { scopes } = props;

  const { t } = useTranslation();

  const scopeElements = useMemo(() => {
    // move guest_read scope to begining of the array so that we can display it first
    moveGuestReadToBeginningOfTheList(scopes);

    // render only non "read" scopes that does not require combination
    const elements = excludeReadScopes(scopes).reduce((arr, scope) => {
      // fallback logic: sso scopes translation > oauth scopes transation > emptry string
      const description = t(
        `SSOOnboardFormContent.scopes.${scope}`,
        t(`scopes.${scope}.description`, '')
      );

      // only display translation found scopes
      // avoid breaking on future new scopes that do not have translation
      // e.g: delete_sso_connection scope was currently exposed to other grant type
      // where it should be only specific to client_credentials grant
      //
      // ref: https://github.com/moneytree/mt-guest-service/blob/c964f73b8128b7f540faff999d6fc3aa739edd0d/config/initializers/doorkeeper.rb#L21
      if (description) {
        arr.push(
          <Scope
            key={scope}
            icon={`scope-${scope.replace(/_/g, '-')}`}
            description={description}
          />
        );
      }

      return arr;
    }, [] as ReactElement[]);

    // generate combined read scopes
    const readDescription = readScopesDescription(t, scopes);

    // always order them after guest_read scope but before everything else
    if (readDescription) {
      elements.splice(
        scopes.includes('guest_read') ? 1 : 0,
        0,
        <Scope key="combined" icon="allowance" description={readDescription} />
      );
    }

    return elements;
  }, [scopes]);

  return (
    <div data-testid="sso-scopes" className="scopes">
      {scopeElements}
    </div>
  );
}
